<script setup>
import AnaesthetistAgendaTab from '@/components/anaesthetist/AnaesthetistAgendaTab.vue'
import AnaesthetistDiscoverTab from '@/components/anaesthetist/AnaesthetistDiscoverTab.vue'
import ListingTable from '@/components/dashboard/ListingTable.vue'
import { useUserDisplayName } from '@/composables/useUserDisplayName'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['anaesthetist'],
  },
})

const authStore = useAuthStore()
const { anesthesiologistSpecialistAreaIds, anesthesiologistHospitalIds } = storeToRefs(authStore)
const { mobile } = useDisplay()

const userName = useUserDisplayName('Anaesthetist')
const activeTab = ref(mobile.value ? 'discover' : 'list')

const openDetails = () => { activeTab.value = 'list' }
const goDiscover = () => { activeTab.value = 'discover' }

const listContextFilters = computed(() => ({
  specialistAreas: anesthesiologistSpecialistAreaIds.value,
  specialistAreasLogic: 'or',
  hospitalIds: anesthesiologistHospitalIds.value,
}))
</script>

<template>
  <div class="anaesthetist-dashboard">
    <!-- Header compatto -->
    <div class="anaesthetist-dashboard__header">
      <div>
        <div class="text-h6 font-weight-bold">
          {{ userName }}
        </div>
        <div class="text-caption text-medium-emphasis">
          Anaesthetist
        </div>
      </div>
      <VIcon
        size="32"
        color="primary"
      >
        mdi-hospital-box
      </VIcon>
    </div>

    <!-- Tab principali: Discover / My Schedule / All -->
    <VTabs
      v-model="activeTab"
      class="anaesthetist-dashboard__main-tabs"
      grow
    >
      <VTab
        value="discover"
        class="anaesthetist-dashboard__tab"
      >
        <VIcon
          icon="mdi-lightning-bolt"
          size="18"
          class="me-1"
        />
        Discover
      </VTab>
      <VTab
        value="agenda"
        class="anaesthetist-dashboard__tab"
      >
        <VIcon
          icon="mdi-calendar-today"
          size="18"
          class="me-1"
        />
        My Schedule
      </VTab>
      <VTab
        value="list"
        class="anaesthetist-dashboard__tab"
      >
        <VIcon
          icon="mdi-format-list-bulleted"
          size="18"
          class="me-1"
        />
        All
      </VTab>
    </VTabs>

    <VWindow
      v-model="activeTab"
      :touch="false"
    >
      <!-- ⚡ Discover -->
      <VWindowItem
        value="discover"
        eager
      >
        <AnaesthetistDiscoverTab @open-details="openDetails" />
      </VWindowItem>

      <!-- 📅 My Schedule -->
      <VWindowItem value="agenda">
        <AnaesthetistAgendaTab
          @open-details="openDetails"
          @go-discover="goDiscover"
        />
      </VWindowItem>

      <!-- 📋 All listings -->
      <VWindowItem value="list">
        <ListingTable
          title="All listings"
          store-id="anaesthetist-dashboard-list"
          :context-filters="listContextFilters"
          :lock-context-filters="true"
          :auto-apply-on-mount="true"
        />
      </VWindowItem>
    </VWindow>
  </div>
</template>

<style scoped>
.anaesthetist-dashboard {
  padding-block: 0;
  padding-inline: 0;
}

.anaesthetist-dashboard__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 16px 16px 8px;
}

.anaesthetist-dashboard__main-tabs {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.anaesthetist-dashboard__sub-tabs {
  padding-inline: 8px;
}

.anaesthetist-dashboard__tab {
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: none;
}
</style>
